import type { BusPoint } from '../../domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { tokPx } from '@sunwebgroup/ui-components'
import { Button, FormField, IndeterminateProgressCircle, RadioButtonField, RadioButtonGroup, Sheet, SwitchField, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'

import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useGroupResponsibleStore } from '../../infrastructure/useGroupResponsibleStore'
import { useGroupResponsibleUseCases } from '../dependencies'
import {
  getHasProcessingParticipants,
  getParticipantsEditingData,
} from '../selectors'
import { getEditDeadLine } from '../selectors/commonSelectors'

const RadioButtonGroupStyled = styled(RadioButtonGroup)`
  & > :not(:last-child) {
    margin-bottom: ${tokPx('primitives_space_8')};
  }
`

const SwitchFieldStyled = styled(SwitchField)`
  margin-bottom: ${tokPx('primitives_space_16')};
`

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_16')};
  margin-top: ${tokPx('primitives_space_16')};
`

const DeadlineBannerStyled = styled(Banner)`
  margin: ${tokPx('primitives_space_16')} 0;
`

const ParticipantStatusBannerStyled = styled(Banner)`
  margin-top: ${tokPx('primitives_space_12')};
`

const disabledStyle = css`
  opacity: 0.25;
  pointer-events: none;
`

const SheetContentStyled = styled(Sheet.Content)<{ $disabled?: boolean }>`
  ${({ $disabled }) => $disabled && disabledStyle}
`

const SheetHeaderStyled = styled(Sheet.Header)<{ $disabled?: boolean }>`
  ${({ $disabled }) => $disabled && disabledStyle}
`

const IndeterminateProgressCircleStyled = styled(IndeterminateProgressCircle)`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`

export function EditBusSheetPortal() {
  const intl = useIntl()
  const { selectedCount, totalCount } = useGroupResponsibleStore(getParticipantsEditingData)
  const locale = useConfiguration('locale')
  const {
    deadLineDate,
    daysBefore,
    isOverDeadline,
  } = useGroupResponsibleStore(state => getEditDeadLine(state, locale, 'bus'))
  const {
    busPickupPoints,
    busReturnPoints,
    isEditingParticipants,
    toggleIsEditingParticipants,
    busAssignment,
    setIn,
    retrieveBusPointsData,
  } = useGroupResponsibleStore(state => state)
  const hasProcessingParticipants = useGroupResponsibleStore(getHasProcessingParticipants)
  const useCases = useGroupResponsibleUseCases()

  const handleSameAsPickupToggle = (checked: boolean) => {
    setIn('busAssignment.sameAsPickup', checked)

    if (checked) {
      setIn('busAssignment.returnPointNotSelected', false)
    }
    if (busAssignment.selectedPickupPointId && checked) {
      const pickUpSelected = busPickupPoints.find(point => point.id === busAssignment.selectedPickupPointId)
      if (pickUpSelected?.hasSameBusReturnPoint) {
        setIn('busAssignment.selectedReturnPointId', pickUpSelected?.sameBusReturnPoint || '')
        setIn('busAssignment.returnPointNotSelected', false)
      }
      else {
        setIn('busAssignment.sameAsPickup', false)
        setIn('busAssignment.returnPointNotSelected', true)
      }
    }
  }

  const handlePickupChange = (pointId: string) => {
    setIn('busAssignment.selectedPickupPointId', pointId)
    setIn('busAssignment.pickupPointNotSelected', false)
    const pickUpSelected = busPickupPoints.find(point => point.id === pointId)
    if (busAssignment.sameAsPickup && pickUpSelected?.hasSameBusReturnPoint) {
      setIn('busAssignment.selectedReturnPointId', pickUpSelected?.sameBusReturnPoint ?? '')
      setIn('busAssignment.returnPointNotSelected', false)
    }
    else {
      setIn('busAssignment.sameAsPickup', false)
    }
  }

  const handleReturnChange = (pointId: string) => {
    setIn('busAssignment.selectedReturnPointId', pointId)
    setIn('busAssignment.returnPointNotSelected', false)
  }

  const handleSave = () => {
    useCases.assignBusToParticipants()
  }

  const commonSheetStructure = (content: React.ReactNode) => (
    <Sheet.Root
      open={isEditingParticipants}
      onOpenChange={(open) => {
        if (busAssignment.submitting && !open) {
          return
        }
        toggleIsEditingParticipants(open)
        if (!open) {
          setIn('busAssignment', {
            selectedPickupPointId: '',
            selectedReturnPointId: '',
            sameAsPickup: true,
            submitting: false,
            error: false,
            pickupPointNotSelected: false,
            returnPointNotSelected: false,
          })
        }
      }}
    >
      <Sheet.Body side="right" hasPadding>
        <SheetHeaderStyled
          $disabled={busAssignment.submitting}
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'common_edit' })}
        />
        <SheetContentStyled $disabled={busAssignment.submitting}>
          <Typography variant="display-h-regular">
            {intl.formatMessage(
              {
                id: 'groups_responsible_bus_selected_count',
              },
              {
                selected: selectedCount,
                total: totalCount,
              },
            )}
          </Typography>
          {content}
        </SheetContentStyled>
        {busAssignment.submitting && (
          <IndeterminateProgressCircleStyled />
        )}
      </Sheet.Body>
    </Sheet.Root>
  )

  const renderBusPoints = (points: BusPoint[], isPickup: boolean) => {
    if (points.length === 1) {
      return (
        <Banner
          color="informative"
          size="M"
          hasIcon
          icon="MapPinOnMap"
          contentIsCentered
        >
          <Banner.TextsWrapper>
            <Banner.Text>
              {intl.formatMessage(
                { id: 'groups_responsible_single_bus_point' },
                { location: points[0].name },
              )}
            </Banner.Text>
          </Banner.TextsWrapper>
        </Banner>
      )
    }

    const selectedValue = isPickup ? busAssignment.selectedPickupPointId : busAssignment.selectedReturnPointId
    const onValueChange = isPickup ? handlePickupChange : handleReturnChange

    return (
      <div>
        <RadioButtonGroupStyled
          value={selectedValue}
          onValueChange={onValueChange}
        >
          {points.map(point => (
            <RadioButtonField
              key={isPickup ? `pickUp-${point.id}` : `return-${point.id}`}
              label={point.name}
              value={point.id}
              variant="outlined"
            />
          ))}
        </RadioButtonGroupStyled>
      </div>
    )
  }

  let statusBannerProps: { text: string, supportingText?: string } | null = null

  if (hasProcessingParticipants) {
    statusBannerProps = {
      text: intl.formatMessage({ id: 'groups_responsible_bus_assignment_processing' }),
      supportingText: intl.formatMessage({ id: 'groups_responsible_bus_assignment_processing_supporting' }),
    }
  }

  if (isOverDeadline) {
    return commonSheetStructure(
      <>
        <DeadlineBannerStyled
          color="warning"
          size="M"
          hasIcon
        >
          <Banner.TextsWrapper>
            <Banner.Text>
              {intl.formatMessage({ id: 'groups_responsible_bus_assignment_is_over_deadline_title' })}
            </Banner.Text>
            <Banner.SupportingText>
              {intl.formatMessage(
                {
                  id: 'groups_responsible_bus_assignment_is_over_deadline_description',
                },
                {
                  deadLineDate,
                  daysBefore,
                },
              )}
            </Banner.SupportingText>
          </Banner.TextsWrapper>
        </DeadlineBannerStyled>
        <Button fullWidth onClick={() => toggleIsEditingParticipants(false)}>
          {intl.formatMessage({ id: 'close' })}
        </Button>
      </>,
    )
  }

  if (retrieveBusPointsData.loading) {
    return commonSheetStructure(
      <div style={{ justifySelf: 'center', marginTop: '64px' }}>
        <IndeterminateProgressCircle />
      </div>,
    )
  }

  if (retrieveBusPointsData.error) {
    return commonSheetStructure(
      <ParticipantStatusBannerStyled
        color="danger"
        size="M"
        hasIcon
        contentIsCentered
      >
        <Banner.TextsWrapper>
          <Banner.Text>
            {intl.formatMessage({ id: 'groups_responsible_get_bus_entries_error' })}
          </Banner.Text>
        </Banner.TextsWrapper>
      </ParticipantStatusBannerStyled>,
    )
  }
  return commonSheetStructure(
    <>
      <Typography variant="body-m-regular-snug" forwardedAs="div">
        {intl.formatMessage(
          {
            id: 'groups_responsible_table_bus_assignment_subtitle',
          },
          {
            deadLineDate,
            daysBefore,
          },
        )}
      </Typography>
      {statusBannerProps && (
        <ParticipantStatusBannerStyled
          color="warning"
          size="M"
          hasIcon
        >
          <Banner.TextsWrapper>
            <Banner.Text>
              {statusBannerProps.text}
            </Banner.Text>
            <Banner.SupportingText>
              {statusBannerProps.supportingText}
            </Banner.SupportingText>
          </Banner.TextsWrapper>
        </ParticipantStatusBannerStyled>
      )}
      {busAssignment.error && (
        <ParticipantStatusBannerStyled
          color="danger"
          size="M"
          hasIcon
          contentIsCentered
        >
          <Banner.TextsWrapper>
            <Banner.Text>
              {intl.formatMessage({ id: 'groups_responsible_bus_assignment_error' })}
            </Banner.Text>
          </Banner.TextsWrapper>
        </ParticipantStatusBannerStyled>
      )}
      <InputsContainer>
        <FormField
          label={intl.formatMessage({ id: 'groups_responsible_pickup_point' })}
          status={busAssignment.pickupPointNotSelected ? 'error' : 'default'}
          feedbackText={busAssignment.pickupPointNotSelected
            ? intl.formatMessage({ id: 'groups_responsible_pickup_point_required' })
            : undefined}
          disabled={busAssignment.submitting}
        >
          {renderBusPoints(busPickupPoints, true)}
        </FormField>
        <FormField
          label={intl.formatMessage({ id: 'groups_responsible_return_point' })}
          status={busAssignment.returnPointNotSelected ? 'error' : 'default'}
          feedbackText={busAssignment.returnPointNotSelected
            ? intl.formatMessage({ id: 'groups_responsible_return_point_required' })
            : undefined}
          disabled={busAssignment.submitting}
        >
          <SwitchFieldStyled
            label={intl.formatMessage({ id: 'groups_responsible_same_as_pickup_point' })}
            variant="outlined"
            checked={busAssignment.sameAsPickup}
            onCheckedChange={handleSameAsPickupToggle}
          />
          {!busAssignment.sameAsPickup && renderBusPoints(busReturnPoints, false)}
        </FormField>
        <Button
          fullWidth
          onClick={handleSave}
        >
          {intl.formatMessage({ id: 'groups_responsible_save' })}
        </Button>
      </InputsContainer>
    </>,
  )
}
